/**
 * StatsHeader — ряд карточек-показателей вкладки (данные из GET /stats).
 * Порядок строго по макету: Бюджет · Оплачено (зелёный) · Остаток · % оплаты (с прогресс-полоской) ·
 * Просрочено (красный) · Ближайший (дата словом + сумма).
 *
 * Значения помечены aria-live="polite" — при live-пересчёте по SSE скринридер озвучит изменение.
 */

import type { StatsDto } from '../../lib/types';
import { formatRub } from '../../lib/money';
import { relDay } from './lib/dates';
import { cx } from './ui/cx';

export interface StatsHeaderProps {
  stats: StatsDto;
}

type CardTone = 'neutral' | 'success' | 'info' | 'danger' | 'warning';

interface StatCard {
  key: string;
  label: string;
  value: string;
  sub?: string | undefined;
  tone: CardTone;
  percent?: number | undefined;
}

export function StatsHeader({ stats }: StatsHeaderProps): JSX.Element {
  const hasOverdue = stats.overdueMinor !== '0';

  const cards: StatCard[] = [
    { key: 'budget', label: 'Бюджет', value: formatRub(stats.totalBudgetMinor), tone: 'neutral' },
    { key: 'paid', label: 'Оплачено', value: formatRub(stats.paidMinor), tone: 'success' },
    { key: 'remainder', label: 'Остаток', value: formatRub(stats.remainderMinor), tone: 'neutral' },
    {
      key: 'percent',
      label: '% оплаты',
      value: `${Math.round(stats.paidPercent)}%`,
      tone: 'info',
      percent: Math.max(0, Math.min(100, stats.paidPercent)),
    },
    {
      key: 'overdue',
      label: 'Просрочено',
      value: formatRub(stats.overdueMinor),
      sub: hasOverdue ? `${stats.overdueCount} тр.` : undefined,
      tone: hasOverdue ? 'danger' : 'neutral',
    },
    {
      key: 'next',
      label: 'Ближайший',
      value: stats.nextPayment ? relDay(stats.nextPayment.plannedDate) : '—',
      sub: stats.nextPayment ? formatRub(stats.nextPayment.plannedAmountMinor) : undefined,
      tone: 'warning',
    },
  ];

  return (
    <div className="pw-stats" role="group" aria-label="Показатели оплат">
      {cards.map((c) => (
        <div key={c.key} className={cx('pw-stat', `pw-stat--${c.tone}`)}>
          <span className="pw-stat__label">{c.label}</span>
          <span className="pw-stat__value pw-num" aria-live="polite">
            {c.value}
          </span>
          {c.percent !== undefined ? (
            <span className="pw-stat__bar" aria-hidden>
              <i style={{ width: `${c.percent}%` }} />
            </span>
          ) : null}
          {c.sub ? <span className="pw-stat__sub pw-num">{c.sub}</span> : null}
        </div>
      ))}
    </div>
  );
}
